import { type JSX, useCallback, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { sleep } from '@chainflip/utils/async';
import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish, isNullish } from '@chainflip/utils/guard';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { type ChainflipToken } from '@/shared/assets/tokens';
import { Link } from '@/shared/components/atoms/Link';
import { DashboardModalWithCloseButton } from '@/shared/components/dashboards/Modal';
import { ReadonlyButton } from '@/shared/components/dashboards/Readonly';
import TokenSelectDropdown from '@/shared/components/dropdown/TokenSelectDropdown';
import TokenInputAmount from '@/shared/components/input/TokenInputAmount';
import { ApyPeriodSelect } from '@/shared/components/lending/ApyPeriodSelect';
import { IncentivisedApyText } from '@/shared/components/lending/IncentivisedApyText';
import SupplyRewardsTooltip, {
  type RewardsTooltipProps,
} from '@/shared/components/lending/SupplyRewardsTooltip';
import { TokenWithChainByAsset } from '@/shared/components/logo/TokenWithChain';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import { useIncentives } from '@/shared/hooks/useIncentives';
import { useLendingConfig } from '@/shared/hooks/useLendingConfig';
import { useLendingPools } from '@/shared/hooks/useLendingPools';
import { useLpLendingPositions } from '@/shared/hooks/useLpLendingPositions';
import { useOraclePrice } from '@/shared/hooks/useOraclePrice';
import { usePolkadot } from '@/shared/hooks/usePolkadot';
import ArrowIconLarge from '@/shared/icons/flip-ui-kit/large/Arrow';
import ArrowIconSmall from '@/shared/icons/flip-ui-kit/small/Arrow';
import Fire from '@/shared/icons/flip-ui-kit/small/Fire';
import LockIcon from '@/shared/icons/flip-ui-kit/small/Lock';
import SliceIcon from '@/shared/icons/flip-ui-kit/small/Slice';
import { apyToText } from '@/shared/utils/boost';
import { defaultAnimationProps } from '@/shared/utils/consts';
import { chainflipAssetMap } from '@/shared/utils/env';
import {
  type ApyAveragePeriodOption,
  calculateLowLtvPenaltyPercent,
  DEFAULT_APY_PERIOD,
  LENDING_POOLS_APY_QUERY,
  SUPPLY_APY_PERIODS,
} from '@/shared/utils/lending';
import { formatUsdValue, pipsToPercent } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import useLoanAccount from 'packages/liquidity-provision/hooks/useLoanAccount';
import {
  useExtrinsicSDK,
  useStateChainAccount,
} from 'packages/liquidity-provision/hooks/useStateChainAccount';
import SuccessIllustration from '../../../public/success-illustration.svg';
import { LpFundAccountCallout } from '../../LpFundAccountButton';
import { RewardsCallout } from '../../Rewards/RewardsCallout';

const Success = ({
  asset,
  amount,
  children,
  amountUsd,
}: {
  asset: ChainflipAsset;
  amount: TokenAmount;
  children: React.ReactNode;
  amountUsd: number;
}) => (
  <div className="flex flex-col gap-6 rounded-md">
    <div className="my-auto flex w-full flex-col items-center justify-center">
      <div className="mb-[-74px]">
        <SuccessIllustration />
      </div>
      <div className="flex flex-col items-center gap-y-2 font-aeonikMedium">
        <span className="text-24 text-cf-white">Funds supplied</span>
        <span className="flex items-center gap-x-1">
          You added {amount.toFormatted()}
          <TokenWithChainByAsset asset={asset} />
          {chainflipAssetMap[asset].symbol}
          <span className="text-cf-light-2">({formatUsdValue(amountUsd)})</span>
        </span>
      </div>
    </div>
    {children}
  </div>
);

const PositionInfoBox = ({
  selectedToken,
  currentPosition,
  projectedPosition,
  currentPoolSharePercent,
  projectedPoolSharePercent,
  borrowCapacityUsd,
  projectedBorrowCapacityUsd,
  penaltyFeePercentDiff,
  currentApy,
  apyPeriod,
  onApyPeriodChange,
  currentEarnings,
  projectedEarnings,
  isTokenSelectOpen,
  rewardsData,
}: {
  selectedToken: ChainflipToken;
  currentPosition: TokenAmount;
  projectedPosition: TokenAmount | undefined;
  currentPoolSharePercent: number;
  projectedPoolSharePercent?: number;
  borrowCapacityUsd: number;
  projectedBorrowCapacityUsd?: number;
  penaltyFeePercentDiff?: number;
  currentApy: number;
  apyPeriod: ApyAveragePeriodOption;
  onApyPeriodChange?: (period: ApyAveragePeriodOption) => void;
  currentEarnings: TokenAmount;
  projectedEarnings?: TokenAmount | undefined;
  isTokenSelectOpen: boolean;
  rewardsData?: RewardsTooltipProps;
}) => (
  <>
    <div
      className={classNames(
        'flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium transition duration-100 ease-in',
        isTokenSelectOpen && 'opacity-50',
      )}
    >
      <div className="flex flex-col gap-y-2">
        <div className="flex items-center gap-x-1 text-14 text-cf-light-2">
          <span>Projected</span>
          {onApyPeriodChange ? (
            <ApyPeriodSelect
              periods={SUPPLY_APY_PERIODS}
              selected={apyPeriod}
              onChange={onApyPeriodChange}
              contentClassname="!z-[100]"
            />
          ) : (
            <span>{apyPeriod.label}</span>
          )}
          <span>APY</span>
          <QuestionMarkTooltip
            content={`Annual Percentage Yield (APY) estimates the annual return in a lending pool. We take hourly snapshots of fees earned in the pool, average them over ${apyPeriod.description}, and project the result over a year.`}
          />
        </div>
        <div className="flex items-center gap-x-2 text-16 text-cf-light-2">
          {rewardsData ? (
            <IncentivisedApyText
              tooltipContent={<SupplyRewardsTooltip {...rewardsData} />}
              totalApy={rewardsData.baseApy + rewardsData.rewardsApy}
              baseApy={rewardsData.baseApy}
              rewardsApy={rewardsData.rewardsApy}
              className="w-auto"
            />
          ) : (
            <span className="text-cf-white">{apyToText(currentApy)}</span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-y-2 text-cf-light-2">
        <span className="text-14">Projected annual rewards</span>
        <div className="flex items-center gap-x-1 text-16">
          <span className="text-cf-green-3">
            +{(projectedEarnings ?? currentEarnings).toFormatted()} {selectedToken.symbol}
          </span>
        </div>
      </div>
      <div className="flex flex-col gap-y-2 text-cf-light-2">
        <span className="text-14">Projected position size</span>
        <div
          className={classNames(
            'flex items-center gap-x-1 text-16',
            !projectedPosition && 'text-cf-white',
          )}
        >
          {currentPosition.toFormatted()} {selectedToken.symbol}
          {projectedPosition && (
            <>
              <ArrowIconLarge />
              <span className="text-cf-white">
                {projectedPosition?.toFormatted()} {selectedToken.symbol}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-y-2 text-cf-light-2">
        <span className="text-14">Borrow capacity</span>
        <div
          className={classNames(
            'flex items-center gap-x-1 text-16',
            isNullish(projectedBorrowCapacityUsd) && 'text-cf-white',
          )}
        >
          {formatUsdValue(borrowCapacityUsd)}
          {isNotNullish(projectedBorrowCapacityUsd) && (
            <>
              <ArrowIconLarge />
              <span className="text-cf-white">{formatUsdValue(projectedBorrowCapacityUsd)}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center gap-x-2 rounded-md border-[#1C3E6F] bg-[#1C2F4A] p-2 text-12 text-cf-blue-2">
        <Fire />
        You can now use your yield-generating supply as collateral
      </div>
      {isNotNullish(penaltyFeePercentDiff) && penaltyFeePercentDiff >= 0.001 && (
        <div className="flex flex-wrap items-center gap-x-1 text-12 text-cf-light-2">
          Supplying without borrowing increases your loan APR by {penaltyFeePercentDiff.toFixed(3)}%
          <div>
            (
            <Link
              href="https://docs.chainflip.io/lending/fees#low-ltv-penalty"
              target="_blank"
              className="text-cf-light-2"
              underline
            >
              low LTV penalty
            </Link>
            )
          </div>
        </div>
      )}
    </div>
    <div className="flex flex-col gap-y-4 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <SliceIcon />
          {isNotNullish(projectedPoolSharePercent) && 'Projected '}Pool Share
        </div>
        <div className="flex items-center gap-x-2 text-13 text-cf-white">
          <span
            className={classNames(isNotNullish(projectedPoolSharePercent) && 'text-cf-light-2')}
          >
            {currentPoolSharePercent.toFixed(2)}%
          </span>
          {isNotNullish(projectedPoolSharePercent) && (
            <>
              <ArrowIconSmall className="text-cf-light-2" />{' '}
              <span>{projectedPoolSharePercent.toFixed(2)}%</span>
            </>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-x-1">
          <LockIcon />
          Lockup Period
          <QuestionMarkTooltip content="No lock-up period. You can withdraw your liquidity at any time." />
        </div>
        <div className="text-cf-white">None</div>
      </div>
    </div>
  </>
);
interface SupplyModalProps {
  open: boolean;
  onClose: () => void;
  selectedToken: ChainflipToken;
  setSelectedToken: (token: ChainflipToken) => void;
  openedFromBorrow?: boolean;
  onBorrowClick?: () => void;
}

export const SupplyModal = ({
  open,
  onClose,
  selectedToken,
  setSelectedToken,
  openedFromBorrow,
  onBorrowClick,
}: SupplyModalProps): JSX.Element => {
  const { selectedAccount } = usePolkadot(true);
  const { freeBalances, refreshAccountInfo, isLoading: isBalanceLoading } = useStateChainAccount();
  const focusRef = useRef<HTMLButtonElement>(null);
  const [error, setError] = useState<string>('');
  const [emptyAmountError, setEmptyAmountError] = useState<string>();
  const [amount, setAmount] = useState<TokenAmount>();
  const [supplyApyPeriod, setSupplyApyPeriod] =
    useState<ApyAveragePeriodOption>(DEFAULT_APY_PERIOD);
  const [isTokenSelectOpen, setIsTokenSelectOpen] = useState(false);
  const { toast, sendExtrinsic } = useExtrinsicSDK('addLenderFunds');
  const router = useRouter();
  const [prevValues, setPrevValues] = useState<{
    amount: TokenAmount | undefined;
    position: TokenAmount;
    poolSharePercent: number;
    borrowCapacityUsd: number;
  }>();

  const price = useOraclePrice(selectedToken.chainflipId);

  const queryClient = useQueryClient();

  const handleTokenInput = useCallback((value: TokenAmount | undefined) => {
    setAmount(value);
    if (value?.gt(0)) setEmptyAmountError(undefined);
  }, []);

  const { activeSeason } = useIncentives();
  const supplyIncentive = activeSeason?.pools?.find(
    (pool) => pool.asset === selectedToken.chainflipId && pool.type === 'SUPPLY',
  );

  const { lpPositions } = useLpLendingPositions({
    idSs58: selectedAccount.idSs58,
  });

  const {
    poolAssets,
    poolBalances,
    lendingPools,
    isLoading: isPoolBalanceLoading,
  } = useLendingPools();

  const {
    minSupplyAmountUsd,
    targetLtvThresholdBps,
    lowLtvPenaltyThresholdBps,
    lowLtvPenaltyBpsAtMaxLtv,
  } = useLendingConfig();

  const tokensWithBalances = useMemo(() => {
    if (isPoolBalanceLoading) return [];

    return poolAssets.map((asset) => {
      const apy = lendingPools.find((pool) => pool.asset === asset)?.defaultProjectedSupplyApy ?? 0;
      const supplyRewardsApy =
        activeSeason?.pools.find((pool) => pool.asset === asset && pool.type === 'SUPPLY')?.apy ??
        0;
      return {
        token: chainflipAssetMap[asset],
        balance: freeBalances?.[asset] ?? TokenAmount.ZERO,
        totalApy: apy + supplyRewardsApy,
        hasRewards: Boolean(supplyRewardsApy),
      };
    });
  }, [poolAssets, freeBalances, isPoolBalanceLoading, lendingPools, activeSeason]);

  const selectedTokenAvailableBalance =
    freeBalances?.[selectedToken.chainflipId] ?? TokenAmount.ZERO;

  const {
    loanAccount: {
      totalBorrowAmountUsd: currentTotalBorrowAmountUsd,
      totalCollateralBalanceUsd: currentTotalCollateralBalanceUsd,
    },
    penaltyFeePercent: currentPenaltyFeePercent,
  } = useLoanAccount();

  const {
    poolSharePercent,
    selectedTokenCurrentPosition,
    currentEarnings,
    totalApy,
    baseApy,
    currentBorrowCapacityUsd,
  } = useMemo(() => {
    const lpPosition = lpPositions[selectedToken.chainflipId];
    const apyByPeriod = lendingPools.find(
      (pool) => pool.asset === selectedToken.chainflipId,
    )?.projectedSupplyApyByPeriod;
    const apy = apyByPeriod?.[supplyApyPeriod.value] ?? 0;
    const rewardsApy = supplyIncentive?.apy ?? 0;
    const totalCombinedApy = apy + rewardsApy;

    return {
      selectedTokenCurrentPosition: lpPosition?.totalAmount ?? TokenAmount.ZERO,
      poolSharePercent: lpPosition?.poolSharePercent ?? 0,
      currentEarnings: lpPosition?.totalAmount.mul(totalCombinedApy).div(100) ?? TokenAmount.ZERO,
      totalApy: totalCombinedApy,
      baseApy: apy,
      currentBorrowCapacityUsd: Math.max(
        (currentTotalCollateralBalanceUsd * (targetLtvThresholdBps ?? 0)) / 10_000 -
          currentTotalBorrowAmountUsd,
        0,
      ),
    };
  }, [
    lpPositions,
    selectedToken,
    lendingPools,
    supplyApyPeriod,
    supplyIncentive,
    currentTotalCollateralBalanceUsd,
    targetLtvThresholdBps,
    currentTotalBorrowAmountUsd,
  ]);

  const rewardsData = supplyIncentive
    ? {
        baseApy,
        rewardsApy: supplyIncentive.apy,
        seasonIndex: activeSeason?.seasonIndex ?? 0,
        dailyPoints: supplyIncentive.dailyPoints,
      }
    : undefined;

  const {
    mutate,
    isPending,
    isSuccess,
    reset: resetMutation,
  } = useMutation({
    mutationFn: async () => {
      setPrevValues({
        amount,
        position: selectedTokenCurrentPosition,
        poolSharePercent,
        borrowCapacityUsd: currentBorrowCapacityUsd,
      });

      return sendExtrinsic(selectedToken.chainflipId, amount?.toString() ?? '');
    },
    async onSuccess() {
      await sleep(500);
      await Promise.all(
        [LENDING_POOLS_APY_QUERY, 'cf_lending_pools', 'cf_account_info'].map((query) =>
          queryClient.invalidateQueries({ queryKey: [query], exact: false }),
        ),
      );
      await refreshAccountInfo();
      toast.success(
        `Liquidity was added to ${chainflipAssetMap[selectedToken.chainflipId].symbol} lending pool`,
      );
    },
    onError(err) {
      setPrevValues(undefined);
      toast.error('Error while submitting transaction', { description: err.message });
    },
  });

  const {
    projectedPoolSharePercent,
    projectedPosition,
    projectedEarnings,
    projectedBorrowCapacityUsd,
    projectedPenaltyFeePercent,
  } = useMemo(() => {
    if (
      !amount ||
      amount.eq(0) ||
      !selectedToken ||
      (isPending && prevValues?.position.lt(selectedTokenCurrentPosition))
    )
      return {};

    const currentPoolTotalAmount =
      poolBalances?.[selectedToken.chainflipId]?.totalAmount ?? TokenAmount.ZERO;
    const projectedPoolTotalAmount = currentPoolTotalAmount.add(amount);
    const positionAfter = amount.add(selectedTokenCurrentPosition);

    const projectedCollateralBalanceUsd =
      currentTotalCollateralBalanceUsd + amount.toUsdNumber(price);

    // Supplying more collateral lowers the LTV, which can increase the low-LTV penalty fee
    const projectedLtvPercent =
      projectedCollateralBalanceUsd > 0
        ? (currentTotalBorrowAmountUsd / projectedCollateralBalanceUsd) * 100
        : 0;

    return {
      projectedPoolSharePercent: projectedPoolTotalAmount.eq(0)
        ? 0
        : positionAfter.ratio(projectedPoolTotalAmount).multipliedBy(100).toNumber(),
      projectedPosition: positionAfter,
      projectedEarnings: positionAfter.mul(totalApy).div(100),
      projectedBorrowCapacityUsd: Math.max(
        (projectedCollateralBalanceUsd * (targetLtvThresholdBps ?? 0)) / 10_000 -
          currentTotalBorrowAmountUsd,
        0,
      ),
      projectedPenaltyFeePercent: calculateLowLtvPenaltyPercent(
        projectedLtvPercent,
        pipsToPercent(lowLtvPenaltyThresholdBps),
        pipsToPercent(lowLtvPenaltyBpsAtMaxLtv),
      ),
    };
  }, [
    selectedToken,
    amount,
    poolBalances,
    totalApy,
    selectedTokenCurrentPosition,
    prevValues,
    isPending,
    currentTotalCollateralBalanceUsd,
    targetLtvThresholdBps,
    currentTotalBorrowAmountUsd,
    lowLtvPenaltyThresholdBps,
    lowLtvPenaltyBpsAtMaxLtv,
    price,
  ]);

  // Absolute change between the current and projected low-LTV penalty fee
  const penaltyFeePercentDiff = isNotNullish(projectedPenaltyFeePercent)
    ? Math.abs(projectedPenaltyFeePercent - currentPenaltyFeePercent)
    : undefined;

  const ctaClickHandler = () => {
    if (!amount || amount.lte(0)) {
      setEmptyAmountError('Please enter a valid amount');
      return;
    }
    if (amount.toUsdNumber(price) < minSupplyAmountUsd) {
      setError(`Please deposit an amount higher than the minimum: $${minSupplyAmountUsd}`);
      return;
    }
    mutate();
  };

  return (
    <DashboardModalWithCloseButton
      initialFocus={focusRef}
      open={open}
      onClose={onClose}
      afterLeave={() => {
        setAmount(undefined);
        setError('');
        setEmptyAmountError(undefined);
        setPrevValues(undefined);
        setSupplyApyPeriod(DEFAULT_APY_PERIOD);
        resetMutation();
      }}
      topContent={supplyIncentive ? <RewardsCallout /> : undefined}
    >
      <AnimatePresence mode="wait">
        {isSuccess ? (
          <motion.div key="supply-success" {...defaultAnimationProps}>
            <Success
              asset={selectedToken.chainflipId}
              amount={prevValues?.amount ?? TokenAmount.ZERO}
              amountUsd={(prevValues?.amount ?? TokenAmount.ZERO).toUsdNumber(price)}
            >
              <PositionInfoBox
                selectedToken={selectedToken}
                currentPosition={prevValues?.position ?? TokenAmount.ZERO}
                projectedPosition={selectedTokenCurrentPosition}
                currentPoolSharePercent={prevValues?.poolSharePercent ?? 0}
                currentEarnings={currentEarnings}
                projectedPoolSharePercent={poolSharePercent}
                currentApy={totalApy}
                apyPeriod={supplyApyPeriod}
                isTokenSelectOpen={isTokenSelectOpen}
                rewardsData={rewardsData}
                borrowCapacityUsd={prevValues?.borrowCapacityUsd ?? 0}
                projectedBorrowCapacityUsd={currentBorrowCapacityUsd}
              />
              <div className="flex flex-col gap-y-3">
                <ReadonlyButton
                  className="w-full"
                  type="primary-standard"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    if (onBorrowClick) return onBorrowClick();
                    return router.push('/lending?tab=borrow');
                  }}
                >
                  Borrow
                </ReadonlyButton>
                <ReadonlyButton
                  className="w-full"
                  type="secondary-standard"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                    return router.push('/lending?tab=supply');
                  }}
                >
                  View my Position
                </ReadonlyButton>
              </div>
            </Success>
          </motion.div>
        ) : (
          <motion.div key="supply-form" {...defaultAnimationProps}>
            <div className="flex max-w-[437px] flex-col gap-y-6">
              <div className="flex flex-col gap-y-2">
                <div className="font-aeonikMedium text-16 text-cf-white">Supply</div>
                {openedFromBorrow && (
                  <div className="text-14 leading-[18px] text-cf-light-2">
                    To borrow, top up your pool supply first. It will be used as collateral.
                  </div>
                )}
              </div>
              <div className="h-px bg-cf-gray-4" />
              <LpFundAccountCallout />
              <TokenSelectDropdown
                isOpen={isTokenSelectOpen}
                setIsOpen={setIsTokenSelectOpen}
                selectedToken={selectedToken}
                setSelectedToken={(token) => {
                  setAmount(undefined);
                  setError('');
                  setEmptyAmountError(undefined);
                  setSelectedToken(token);
                }}
                tokensWithBalances={tokensWithBalances}
                disabled={isPending}
              />
              <TokenInputAmount
                key={selectedToken.chainflipId}
                label="Amount"
                token={selectedToken}
                balance={selectedTokenAvailableBalance}
                value={amount}
                onChange={handleTokenInput}
                disabled={!selectedToken || isPending}
                error={error || emptyAmountError}
                setError={setError}
                focusOnMount
                isBalanceLoading={isBalanceLoading}
                balanceLabel="Account Balance"
                showError={!isPending}
              />
              <PositionInfoBox
                selectedToken={selectedToken}
                currentPosition={selectedTokenCurrentPosition}
                projectedPosition={projectedPosition}
                currentPoolSharePercent={poolSharePercent}
                projectedPoolSharePercent={projectedPoolSharePercent}
                currentApy={totalApy}
                apyPeriod={supplyApyPeriod}
                onApyPeriodChange={setSupplyApyPeriod}
                currentEarnings={currentEarnings}
                projectedEarnings={projectedEarnings}
                isTokenSelectOpen={isTokenSelectOpen}
                rewardsData={rewardsData}
                borrowCapacityUsd={currentBorrowCapacityUsd}
                projectedBorrowCapacityUsd={projectedBorrowCapacityUsd}
                penaltyFeePercentDiff={penaltyFeePercentDiff}
              />
              <ReadonlyButton
                fullWidth
                onClick={ctaClickHandler}
                loading={isPending && 'iconOnly'}
                disabled={Boolean(emptyAmountError) || Boolean(error) || isPending}
              >
                Confirm & Supply
              </ReadonlyButton>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DashboardModalWithCloseButton>
  );
};
